// Pure helpers of the calendar screens (COM-FE-04): the days of each view in Africa/Tunis, the
// events of a day, the layout of overlapping events in a day column, the texts of dates and times,
// and plain-language error messages. Calendar days are `YYYY-MM-DD` strings, never Date objects
// in the browser's own time zone.
import type { CalendarEventDtoOutput } from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import {
  EVENT_KINDS,
  addDays,
  localDateOf,
  localInstant,
  type EventKind,
} from '@batiflow/contracts';
import { intlLocale, type AppLocale } from '@batiflow/i18n';
import type { TFunction } from 'i18next';
import { CALENDAR_VIEWS, isDay, isKind, type CalendarView } from './search';

export type CalendarEvent = CalendarEventDtoOutput;
export type Translate = TFunction<'calendar'>;
export type { EventKind };

export const TIME_ZONE = 'Africa/Tunis';
export const KINDS: readonly EventKind[] = EVENT_KINDS;

export { CALENDAR_VIEWS, isDay, isKind, type CalendarView };
export const DEFAULT_VIEW: CalendarView = 'week';

export const etagOf = (version: number): string => `W/"${String(version)}"`;

// --- Days (YYYY-MM-DD, calendar arithmetic without any zone).

export const todayOf = (now: Date = new Date()): string => localDateOf(now);

/** 0 = Monday … 6 = Sunday: the week of Tunisia starts on Monday. */
export function weekdayIndex(day: string): number {
  return (new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7;
}

export const startOfWeek = (day: string): string => addDays(day, -weekdayIndex(day));
export const startOfMonth = (day: string): string => `${day.slice(0, 7)}-01`;

export function daysInMonth(day: string): number {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7));
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export const endOfMonth = (day: string): string =>
  `${day.slice(0, 7)}-${String(daysInMonth(day)).padStart(2, '0')}`;

/** The same day of another month (clamped to the shorter month: 31 March + 1 month = 30 April). */
export function addMonths(day: string, months: number): string {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7)) - 1 + months;
  const first = new Date(Date.UTC(year, month, 1));
  const prefix = first.toISOString().slice(0, 7);
  const length = daysInMonth(`${prefix}-01`);
  const clamped = Math.min(Number(day.slice(8, 10)), length);
  return `${prefix}-${String(clamped).padStart(2, '0')}`;
}

export const sameMonth = (a: string, b: string): boolean => a.slice(0, 7) === b.slice(0, 7);

export interface DayWindow {
  /** First and last day shown (inclusive). */
  first: string;
  last: string;
  /** The days shown, in order. */
  days: string[];
}

const range = (first: string, count: number): string[] =>
  Array.from({ length: count }, (_, index) => addDays(first, index));

/** The whole weeks that cover the month of `day` (5 or 6 rows of 7 days). */
export function monthGrid(day: string): string[] {
  const count = Math.ceil((weekdayIndex(startOfMonth(day)) + daysInMonth(day)) / 7) * 7;
  return range(startOfWeek(startOfMonth(day)), count);
}

/** The days a view shows for an anchor day. */
export function windowOf(view: CalendarView, day: string): DayWindow {
  const days =
    view === 'week'
      ? range(startOfWeek(day), 7)
      : view === 'workWeek'
        ? range(startOfWeek(day), 5)
        : view === 'month'
          ? monthGrid(day)
          : range(startOfMonth(day), daysInMonth(day));
  return { first: days[0] ?? day, last: days[days.length - 1] ?? day, days };
}

/** The instants a window covers, as the API takes them: `from` inclusive, `to` exclusive. */
export function instantsOf(window: DayWindow): { from: string; to: string } {
  return {
    from: localInstant(window.first, 0).toISOString(),
    to: localInstant(addDays(window.last, 1), 0).toISOString(),
  };
}

/** The anchor day after one step back (-1) or forward (+1) in a view. */
export function stepDay(view: CalendarView, day: string, direction: -1 | 1): string {
  return view === 'week' || view === 'workWeek'
    ? addDays(day, 7 * direction)
    : addMonths(day, direction);
}

// --- Events and days.

/** First and last day (inclusive, Africa/Tunis) an event touches. */
export function eventDays(event: CalendarEvent): { first: string; last: string } {
  const start = new Date(event.startsAt);
  const end = new Date(event.endsAt);
  const first = localDateOf(start);
  // `endsAt` is exclusive: an event that ends at midnight does not touch the next day.
  const last = localDateOf(new Date(Math.max(end.getTime() - 1, start.getTime())));
  return { first, last: last < first ? first : last };
}

/** Events by start time, all-day ones first. */
export function sortEvents(events: readonly CalendarEvent[]): CalendarEvent[] {
  return [...events].sort(
    (a, b) =>
      Number(b.allDay) - Number(a.allDay) ||
      Date.parse(a.startsAt) - Date.parse(b.startsAt) ||
      a.title.localeCompare(b.title),
  );
}

/** The events of each day of `days`: an event that spans several days appears on each of them. */
export function groupByDay(
  events: readonly CalendarEvent[],
  days: readonly string[],
): Map<string, CalendarEvent[]> {
  const byDay = new Map<string, CalendarEvent[]>(days.map((day) => [day, []]));
  for (const event of sortEvents(events)) {
    const { first, last } = eventDays(event);
    for (const day of days) {
      if (day >= first && day <= last) byDay.get(day)?.push(event);
    }
  }
  return byDay;
}

// --- Filters.

export interface Filters {
  hiddenKinds: readonly EventKind[];
  /** A project id, or null for every project. */
  projectId: string | null;
}

export function applyFilters(events: readonly CalendarEvent[], filters: Filters): CalendarEvent[] {
  return events.filter(
    (event) =>
      !filters.hiddenKinds.includes(event.kind) &&
      (filters.projectId === null || event.projectId === filters.projectId),
  );
}

/** The projects that have events in the loaded window, for the project filter. */
export function projectsOf(
  events: readonly CalendarEvent[],
): { id: string; code: string | null; name: string }[] {
  const seen = new Map<string, { id: string; code: string | null; name: string }>();
  for (const event of events) {
    if (event.projectId && !seen.has(event.projectId))
      seen.set(event.projectId, {
        id: event.projectId,
        code: event.projectCode,
        name: event.projectName ?? event.projectCode ?? event.projectId,
      });
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}

// --- Time grid layout (week and work week).

export const MINUTES_PER_DAY = 24 * 60;
/** A very short event is drawn at least this tall so it can be read and clicked. */
export const MIN_VISIBLE_MINUTES = 30;

export interface Placed {
  event: CalendarEvent;
  startMinute: number;
  endMinute: number;
  column: number;
  columns: number;
}

/** The part of a timed event that falls on `day`, in minutes from midnight Africa/Tunis. */
function minutesOnDay(event: CalendarEvent, day: string): { start: number; end: number } {
  const dayStart = localInstant(day, 0).getTime();
  const dayEnd = localInstant(addDays(day, 1), 0).getTime();
  const start = Math.max(Date.parse(event.startsAt), dayStart);
  const end = Math.min(Date.parse(event.endsAt), dayEnd);
  const startMinute = Math.round((start - dayStart) / 60_000);
  const endMinute = Math.min(
    MINUTES_PER_DAY,
    Math.max(Math.round((end - dayStart) / 60_000), startMinute + MIN_VISIBLE_MINUTES),
  );
  return { start: startMinute, end: endMinute };
}

/**
 * Lays the timed events of one day side by side: events that overlap share the width of the day
 * column, the others keep all of it.
 */
export function layoutDay(events: readonly CalendarEvent[], day: string): Placed[] {
  const items = events
    .filter((event) => !event.allDay)
    .map((event) => ({ event, ...minutesOnDay(event, day) }))
    .sort((a, b) => a.start - b.start || b.end - a.end);
  const placed: Placed[] = [];
  let cluster: { event: CalendarEvent; start: number; end: number; column: number }[] = [];
  let clusterEnd = -1;
  const flush = () => {
    const columns = cluster.reduce((max, item) => Math.max(max, item.column + 1), 1);
    for (const item of cluster)
      placed.push({
        event: item.event,
        startMinute: item.start,
        endMinute: item.end,
        column: item.column,
        columns,
      });
    cluster = [];
    clusterEnd = -1;
  };
  for (const item of items) {
    if (cluster.length > 0 && item.start >= clusterEnd) flush();
    const taken = new Set(
      cluster.filter((other) => other.end > item.start).map((other) => other.column),
    );
    let column = 0;
    while (taken.has(column)) column += 1;
    cluster.push({ ...item, column });
    clusterEnd = Math.max(clusterEnd, item.end);
  }
  flush();
  return placed;
}

// --- Texts.

/** Left-to-right, right-to-left and Arabic letter marks that CLDR puts inside Arabic dates. */
const BIDI_MARKS = [0x200e, 0x200f, 0x061c].map((code) => String.fromCharCode(code));
/** Plain text of a formatted date: no direction marks, every space a normal one. */
const clean = (text: string): string =>
  BIDI_MARKS.reduce((result, mark) => result.replaceAll(mark, ''), text).replace(/\s/g, ' ');

const LRI = String.fromCharCode(0x2066);
const PDI = String.fromCharCode(0x2069);
/** A date or a number inside a sentence keeps its order in Arabic (left-to-right isolate). */
export const isolateLtr = (text: string): string => `${LRI}${text}${PDI}`;

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(
  locale: AppLocale,
  key: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const id = `${locale}|${key}`;
  let found = formatters.get(id);
  if (!found) {
    found = new Intl.DateTimeFormat(intlLocale(locale), options);
    formatters.set(id, found);
  }
  return found;
}

/** A calendar day is shown at noon UTC so that no zone moves it to another day. */
const noon = (day: string): Date => new Date(`${day}T12:00:00Z`);

/** "lundi 12 octobre 2026". */
export const formatDayLong = (day: string, locale: AppLocale): string =>
  clean(
    formatter(locale, 'dayLong', {
      timeZone: 'UTC',
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(noon(day)),
  );

/** "12 octobre 2026". */
export const formatDayMedium = (day: string, locale: AppLocale): string =>
  clean(
    formatter(locale, 'dayMedium', {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(noon(day)),
  );

/** "lun." (the column title of a day). */
export const formatWeekdayShort = (day: string, locale: AppLocale): string =>
  clean(formatter(locale, 'weekdayShort', { timeZone: 'UTC', weekday: 'short' }).format(noon(day)));

/** One letter (the column title of the mini-month). */
export const formatWeekdayNarrow = (day: string, locale: AppLocale): string =>
  clean(
    formatter(locale, 'weekdayNarrow', { timeZone: 'UTC', weekday: 'narrow' }).format(noon(day)),
  );

export const formatWeekdayLong = (day: string, locale: AppLocale): string =>
  clean(formatter(locale, 'weekdayLong', { timeZone: 'UTC', weekday: 'long' }).format(noon(day)));

/** The day number, in Latin digits. */
export const formatDayNumber = (day: string): string => String(Number(day.slice(8, 10)));

/** "octobre 2026". */
export const formatMonthYear = (day: string, locale: AppLocale): string =>
  clean(
    formatter(locale, 'monthYear', { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(
      noon(day),
    ),
  );

/** "09:00" (24-hour clock in every language, Africa/Tunis). */
export const formatClock = (instant: string | Date, locale: AppLocale): string =>
  clean(
    formatter(locale, 'clock', {
      timeZone: TIME_ZONE,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date(instant)),
  );

/** The title of the period a view shows: "12 – 18 octobre 2026", "octobre 2026". */
export function periodLabel(
  view: CalendarView,
  window: DayWindow,
  anchor: string,
  locale: AppLocale,
): string {
  if (view === 'month' || view === 'agenda') return formatMonthYear(anchor, locale);
  return clean(
    formatter(locale, 'range', {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).formatRange(noon(window.first), noon(window.last)),
  );
}

export interface WhenParts {
  /** The day, or the first and last day of an event that spans several. */
  date: string;
  /** "09:00 – 10:30"; null for an all-day event. */
  time: string | null;
}

export function whenParts(event: CalendarEvent, locale: AppLocale): WhenParts {
  const { first, last } = eventDays(event);
  const date =
    first === last
      ? formatDayMedium(first, locale)
      : `${formatDayMedium(first, locale)} – ${formatDayMedium(last, locale)}`;
  return {
    date,
    time: event.allDay
      ? null
      : `${formatClock(event.startsAt, locale)} – ${formatClock(event.endsAt, locale)}`,
  };
}

/** The reading of the date and time of an event for a screen reader. */
export function whenText(event: CalendarEvent, locale: AppLocale, t: Translate): string {
  const parts = whenParts(event, locale);
  return `${parts.date}, ${parts.time ?? t('event.allDay')}`;
}

/** "Réunion : Point de chantier, 12 octobre 2026, 09:00 – 10:30" (the name of an event button). */
export function eventLabel(event: CalendarEvent, locale: AppLocale, t: Translate): string {
  return t(event.readOnly ? 'event.ariaReadOnly' : 'event.aria', {
    kind: t(`kinds.${event.kind}`),
    title: event.title,
    when: whenText(event, locale, t),
  });
}

// --- Forms: dates and times as the dialog holds them.

/** The Tunis wall-clock time of an instant: "09:30". */
export const clockOf = (instant: string): string => formatClock(instant, 'en');

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
export const isClock = (value: string): boolean => TIME_RE.test(value);

/** The instant at which the Tunis wall clock reads `day` `HH:MM`. */
export function instantOf(day: string, clock: string): string {
  const [hours, minutes] = clock.split(':').map(Number) as [number, number];
  return new Date(localInstant(day, hours).getTime() + minutes * 60_000).toISOString();
}

/** The Tunis day of an instant. */
export const dayOf = (instant: string): string => localDateOf(new Date(instant));

// --- Server answers.

export const apiErrorCode = (error: unknown): string | null =>
  error instanceof ApiError ? error.code : null;

/** 412 or 428: the event was changed by someone else meanwhile. */
export const isStaleVersion = (error: unknown): boolean =>
  error instanceof ApiError &&
  (error.status === 412 || error.status === 428 || error.code === 'STALE_VERSION');

/** 403 STEP_UP_REQUIRED: the app opens its own dialog and replays the action. */
export const isStepUpRequired = (error: unknown): boolean =>
  apiErrorCode(error) === 'STEP_UP_REQUIRED';

/** A plain-language message for an API error (never the raw server text). */
export function errorMessage(t: Translate, error: unknown): string {
  if (!(error instanceof ApiError)) return t('errors.network');
  return t(`errors.${error.code}`, {
    defaultValue: t(`common:errors.${error.code}`, { defaultValue: t('common:errors.generic') }),
  });
}

export const calendarPath = (orgId: string): string => `/o/${encodeURIComponent(orgId)}/calendar`;
